import type React from 'react';

import { InviteServices } from '../InviteFriendsPage';

import { InviteByLink } from './InviteByLInk';
import SearchByName from './SearchByName';

import { Button, InputField, NavBar, Tabs } from '@/components';
import useAuth from '@/hooks/useAuth';

interface MobileInviteProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  formData: { name: string; email: string };
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onInputBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  isLoading: boolean;
  errors: { name?: string; email?: string };
  idTorneo: number | null;
  onNavigateToTourney: () => void;
  onInviteById: (userId: number) => Promise<boolean | undefined>;
  services: InviteServices;
  type: 'tournament' | 'cup' | string;
  isDesktop: boolean;
}

export default function MobileInvite({
  activeTab,
  setActiveTab,
  formData,
  onInputChange,
  onInputBlur,
  onSubmit,
  isLoading,
  errors,
  idTorneo,
  onNavigateToTourney,
  onInviteById,
  services,
  type,
  isDesktop,
}: Readonly<MobileInviteProps>) {
  const { userInitialData } = useAuth();
  const handleInvite = (userId: number): Promise<boolean | undefined> => {
    return onInviteById(userId);
  };
  const tabOptions = [
    { name: 'Por Nombre' },
    { name: 'Por E-mail' },
    { name: 'Por Link' },
  ];
  const handleTabChange = (index: number) => {
    const tabMapping = ['name', 'email', 'link'];
    setActiveTab(tabMapping[index]);
  };
  // Función para validar email
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  // Verificar si el email es válido (asegurando que sea un booleano)
  const emailIsValid = Boolean(
    formData.email && isValidEmail(formData.email) && !errors.email
  );
  return (
    <NavBar
      desktop
      mobile={{
        leftContent: {
          backAction: onNavigateToTourney,
        },
        centerContent: {
          title: 'Invitar amigos',
        },
      }}
    >
      <div>
        {/* Tabs */}
        <Tabs
          activeTab={['name', 'email', 'link'].indexOf(activeTab)}
          className="!justify-center [&>*]:!flex-none [&>*]:px-6"
          tabs={tabOptions}
          variant="underline"
          onClick={handleTabChange}
        />
      </div>
      {/* Form Content */}
      <div className="p-4">
        {activeTab === 'name' && (
          <SearchByName
            idTorneo={idTorneo ?? 0}
            isDesktop={isDesktop}
            searchService={services.searchByName}
            suggestUsersService={services.suggestUsers}
            type={type}
            onInvite={handleInvite}
          />
        )}
        {activeTab === 'email' && (
          <form
            className="space-y-6"
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit();
            }}
          >
            {type === 'cup' && (
              <p
                className={`${
                  !isDesktop
                    ? 'text-[#333333] text-base leading-tight mb-6'
                    : 'text-md font-medium text-[#333333] mb-6'
                }`}
              >
                Recordá que el equipo suma con los puntos de tu 11 y el de tus 3
                amigos. Si no está completo, no sumará puntos.
              </p>
            )}
            <InputField
              behavior="default"
              error={errors.name}
              label="Nombre del DT"
              maxLength={50}
              name="name"
              placeholder="Ingresar nombre"
              value={formData.name}
              onBlur={onInputBlur}
              onChange={onInputChange}
            />
            <InputField
              behavior="default"
              error={errors.email}
              label="E-mail del DT"
              name="email"
              placeholder="Ingresar e-mail"
              success={emailIsValid}
              type="email"
              value={formData.email}
              onBlur={onInputBlur}
              onChange={onInputChange}
            />
            <div className="flex justify-center">
              <Button
                className={`w-auto !bg-[#e3eaff] !text-[#3366ff] py-3 ${
                  isLoading ||
                  !formData.name ||
                  !formData.email ||
                  !!errors.name ||
                  !!errors.email
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-[#e3eaff] text-[#3366ff]'
                }`}
                disabled={
                  !formData.name ||
                  !formData.email ||
                  !!errors.name ||
                  !!errors.email ||
                  formData.email === userInitialData?.mail
                }
                type="submit"
                variant="secondary"
              >
                {isLoading ? 'Enviando...' : 'Enviar invitación'}
              </Button>
            </div>
          </form>
        )}
        {activeTab === 'link' && (
          <InviteByLink
            idTorneo={String(idTorneo)}
            isDesktop={isDesktop}
            type={type}
          />
        )}
      </div>
    </NavBar>
  );
}
